// מסך 9 — הגדרות API, הרשאות ועלויות.
import { useState } from 'react';
import { api } from '../api';
import { ErrorBox, Field, Loading, fmtDate, useLoad, useMe } from '../components/ui';
import { PageHead, Tabs, useAction } from '../components/mgmt/common';
import { AuditLog, ROLES, UsageLog, UsersAdmin } from '../components/mgmt/SettingsParts';
import { AiConfigForm } from '../components/mgmt/AiConfigForm';

interface SettingsData {
  settings: {
    aiBudget: { monthlyUsd: number; perRequestUsd: number; perSetUsd: number; maxOutputTokens: number };
    limits: { maxSizesPerSet: number; uploadMaxMb: number };
    checks: { allowOverlapRoles: string[]; defaultMinFontPx: number };
  };
  ai: { provider: string; model: string; effort: string; keyConfigured: boolean; priceInPerM: number; priceOutPerM: number };
  usage: { month_cost: number; input_tokens: number; output_tokens: number; calls: number };
  backup?: { last: { at: string; dir: string; ok: boolean; files: number; problems: string[]; error?: string } | null; dir: string; intervalHours: number };
}
interface AiStatus { provider: string; model: string; effort?: string; mock: boolean; keyConfigured: boolean; verified: boolean; lastTest?: { ok: boolean; at: string; error?: string; model?: string } | null; configError?: string | null }
type Tab = 'ai' | 'limits' | 'usage' | 'users' | 'audit';

export default function Settings() {
  const me = useMe();
  const admin = me.role === 'admin';
  const [tab, setTab] = useState<Tab>('ai');
  const { data, error, loading, reload } = useLoad<SettingsData>('/api/settings');
  const tabs: { id: Tab; label: string }[] = [{ id: 'ai', label: 'חיבור AI ועלויות' }, { id: 'limits', label: 'מגבלות ובדיקות' }];
  if (admin) tabs.push({ id: 'usage', label: 'יומן שימוש AI' }, { id: 'users', label: 'משתמשים והרשאות' }, { id: 'audit', label: 'יומן פעולות' });
  return (
    <div>
      <PageHead title="הגדרות" sub={<>התפקיד שלכם: {ROLES[me.role]}{me.canApproveSpecs && ' · מאשר/ת מפרטים'}{me.canExportDelivery && ' · יצוא למסירה'}</>} />
      <Tabs<Tab> label="אזורי הגדרות" value={tab} onChange={setTab} tabs={tabs} />
      <ErrorBox error={error} />
      {loading && !data && <Loading />}
      <div role="tabpanel">
        {data && tab === 'ai' && <AiPanel data={data} admin={admin} onSaved={reload} />}
        {data && tab === 'limits' && <LimitsPanel data={data} admin={admin} onSaved={reload} />}
        {tab === 'usage' && admin && <div className="card"><UsageLog /></div>}
        {tab === 'users' && admin && <div className="card"><UsersAdmin /></div>}
        {tab === 'audit' && admin && <div className="card"><AuditLog /></div>}
      </div>
    </div>
  );
}

function AiPanel({ data, admin, onSaved }: { data: SettingsData; admin: boolean; onSaved: () => void }) {
  const status = useLoad<AiStatus>('/api/ai/status');
  const [test, setTest] = useState<{ ok: boolean; mock?: boolean; model?: string; latencyMs?: number; error?: string } | null>(null);
  const testAct = useAction();
  const isMock = (status.data?.mock ?? data.ai.provider === 'mock');
  const runTest = async () => { setTest(null); const r = await testAct.run(() => api.post('/api/ai/test-connection')); if (r) { setTest(r); status.reload(); } };
  const b = data.settings.aiBudget;
  const [budget, setBudget] = useState(b);
  const save = useAction();
  const saveBudget = async (e: React.FormEvent) => { e.preventDefault(); if (await save.run(() => api.put('/api/settings/aiBudget', budget), 'התקציב נשמר')) onSaved(); };
  const pctUsed = b.monthlyUsd > 0 ? Math.min(100, (data.usage.month_cost / b.monthlyUsd) * 100) : 0;
  return (
    <div className="grid cols-2" style={{ alignItems: 'start' }}>
      <section className="card stack" aria-labelledby="ai-h">
        <h2 id="ai-h" style={{ margin: 0 }}>חיבור ל-AI</h2>
        {isMock
          ? <div className="alert warn" role="note"><strong>ה-AI עדיין לא מחובר.</strong> העיצובים הם תבניות בסיסיות ולא תוצרי מודל. בחרו ספק והדביקו מפתח בטופס שלמטה.</div>
          : <div className="alert ok" role="status"><strong>מחובר: {status.data?.provider ?? data.ai.provider}</strong> · {status.data?.model ?? data.ai.model}{(status.data?.keyConfigured ?? data.ai.keyConfigured) ? '' : ' · חסר מפתח'}</div>}
        {admin && status.data && <AiConfigForm key={`${status.data.provider}-${status.data.model}`} status={status.data as any} onSaved={() => { status.reload(); onSaved(); }} />}
        {admin ? (
          <div className="stack">
            <div><button className="btn" onClick={runTest} disabled={testAct.busy}>{testAct.busy ? 'בודק חיבור…' : 'בדיקת חיבור'}</button></div>
            <div aria-live="polite">
              <ErrorBox error={testAct.error} />
              {test && (test.ok
                ? <div className="alert ok">החיבור לספק תקין · מודל <span className="mono">{test.model}</span> · {test.latencyMs} ms</div>
                : test.mock
                  ? <div className="alert warn">הבדיקה רצה מול מצב הפיתוח (mock). כדי לחבר באמת, בחרו ספק והדביקו מפתח.</div>
                  : <div className="alert err">החיבור נכשל: {test.error ?? 'שגיאה לא ידועה'}</div>)}
            </div>
          </div>
        ) : <div className="small muted">הגדרת החיבור — למנהל מערכת בלבד.</div>}
        <details>
        <summary className="small muted" style={{ cursor: 'pointer' }}>פרטים טכניים</summary>
        <dl className="kv" style={{ marginTop: 8 }}>
          <dt>ספק</dt><dd className="mono">{status.data?.provider ?? data.ai.provider}</dd>
          <dt>מודל</dt><dd className="mono">{status.data?.model ?? data.ai.model}</dd>
          <dt>רמת מאמץ</dt><dd className="mono">{status.data?.effort ?? data.ai.effort}</dd>
          <dt>מפתח API</dt><dd>{(status.data?.keyConfigured ?? data.ai.keyConfigured) ? <span className="badge ok">מוגדר בשרת</span> : <span className="badge err">לא מוגדר</span>} <span className="small muted">המפתח לעולם אינו מוצג בדפדפן.</span></dd>
          <dt>אימות חיבור</dt><dd>{status.data ? (status.data.verified ? <span className="badge ok">אומת</span> : <span className="badge warn">טרם אומת</span>) : status.error ? <span className="small muted">{isMissingRouteMsg(status.error)}</span> : '…'}</dd>
          {status.data?.lastTest && <><dt>בדיקה אחרונה</dt><dd>{status.data.lastTest.ok ? <span className="badge ok">הצליחה</span> : <span className="badge err">נכשלה</span>} <span className="small muted">{fmtDate(status.data.lastTest.at)}</span>{status.data.lastTest.error && <div className="small" style={{ color: 'var(--danger)' }}>{status.data.lastTest.error}</div>}</dd></>}
          {status.data?.configError && <><dt>שגיאת קונפיגורציה</dt><dd style={{ color: 'var(--danger)' }}>{status.data.configError}</dd></>}
          <dt>מחירון (למיליון יחידות)</dt><dd className="ltr">in ${data.ai.priceInPerM} · out ${data.ai.priceOutPerM}</dd>
        </dl>
        </details>
      </section>

      <section className="card stack" aria-labelledby="cost-h">
        <h2 id="cost-h" style={{ margin: 0 }}>עלויות החודש</h2>
        <div className="grid cols-2">
          <div className="stat"><span className="n ltr">${Number(data.usage.month_cost).toFixed(2)}</span><span className="l">עלות (אומדן לפי מחירון)</span></div>
          <div className="stat"><span className="n">{data.usage.calls}</span><span className="l">פניות ל-AI</span></div>
          <div className="stat"><span className="n ltr">{data.usage.input_tokens.toLocaleString()}</span><span className="l">יחידות קלט</span></div>
          <div className="stat"><span className="n ltr">{data.usage.output_tokens.toLocaleString()}</span><span className="l">יחידות פלט</span></div>
        </div>
        <div>
          <div className="small">ניצול מהתקציב החודשי: {pctUsed.toFixed(0)}%</div>
          <div className="progress" role="progressbar" aria-valuenow={Math.round(pctUsed)} aria-valuemin={0} aria-valuemax={100} aria-label="ניצול תקציב"><span style={{ width: `${pctUsed}%`, background: pctUsed > 85 ? 'var(--danger)' : undefined }} /></div>
        </div>
        <form className="stack" onSubmit={saveBudget} aria-label="תקציבי AI">
          <h3 style={{ margin: 0 }}>תקציבים ותקרות</h3>
          <fieldset disabled={!admin} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
            <div className="grid cols-2">
              <Field label="תקציב חודשי ($)"><input type="number" className="ltr" min={0} step="0.01" value={budget.monthlyUsd} onChange={(e) => setBudget({ ...budget, monthlyUsd: Number(e.target.value) })} /></Field>
              <Field label="תקרה לבקשה ($)"><input type="number" className="ltr" min={0} step="0.01" value={budget.perRequestUsd} onChange={(e) => setBudget({ ...budget, perRequestUsd: Number(e.target.value) })} /></Field>
              <Field label="תקרה לסט ($)"><input type="number" className="ltr" min={0} step="0.01" value={budget.perSetUsd} onChange={(e) => setBudget({ ...budget, perSetUsd: Number(e.target.value) })} /></Field>
              <Field label="מקסימום tokens פלט" hint="1,000–128,000"><input type="number" className="ltr" min={1000} max={128000} step={1000} value={budget.maxOutputTokens} onChange={(e) => setBudget({ ...budget, maxOutputTokens: Number(e.target.value) })} /></Field>
            </div>
          </fieldset>
          <ErrorBox error={save.error} />
          {admin ? <div><button className="btn primary" disabled={save.busy}>{save.busy ? 'שומר…' : 'שמירת תקציב'}</button></div> : <div className="small muted">עריכת תקציבים — למנהל מערכת בלבד.</div>}
        </form>
      </section>
    </div>
  );
}

function isMissingRouteMsg(e: string) { return e.includes('404') || e === 'לא נמצא' ? 'נתיב סטטוס AI אינו זמין עדיין' : e; }

function LimitsPanel({ data, admin, onSaved }: { data: SettingsData; admin: boolean; onSaved: () => void }) {
  const [limits, setLimits] = useState(data.settings.limits);
  const [checks, setChecks] = useState({ ...data.settings.checks, rolesText: data.settings.checks.allowOverlapRoles.join(', ') });
  const l = useAction();
  const c = useAction();
  const saveLimits = async (e: React.FormEvent) => { e.preventDefault(); if (await l.run(() => api.put('/api/settings/limits', limits), 'המגבלות נשמרו')) onSaved(); };
  const saveChecks = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = { defaultMinFontPx: checks.defaultMinFontPx, allowOverlapRoles: checks.rolesText.split(',').map((s) => s.trim()).filter(Boolean) };
    if (await c.run(() => api.put('/api/settings/checks', body), 'הגדרות הבדיקות נשמרו')) onSaved();
  };
  return (
    <div className="grid cols-2" style={{ alignItems: 'start' }}>
      {data.backup && <BackupCard backup={data.backup} admin={admin} onDone={onSaved} />}
      <form className="card stack" onSubmit={saveLimits} aria-labelledby="lim-h">
        <h2 id="lim-h" style={{ margin: 0 }}>מגבלות</h2>
        <fieldset disabled={!admin} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }} className="stack">
          <Field label="מקסימום גדלים לסט"><input type="number" className="ltr" min={1} max={100} value={limits.maxSizesPerSet} onChange={(e) => setLimits({ ...limits, maxSizesPerSet: Number(e.target.value) })} /></Field>
          <Field label="גודל העלאה מרבי (MB)"><input type="number" className="ltr" min={1} max={200} value={limits.uploadMaxMb} onChange={(e) => setLimits({ ...limits, uploadMaxMb: Number(e.target.value) })} /></Field>
        </fieldset>
        <ErrorBox error={l.error} />
        {admin && <div><button className="btn primary" disabled={l.busy}>{l.busy ? 'שומר…' : 'שמירה'}</button></div>}
      </form>
      <form className="card stack" onSubmit={saveChecks} aria-labelledby="chk-h">
        <h2 id="chk-h" style={{ margin: 0 }}>בדיקות אוטומטיות</h2>
        <fieldset disabled={!admin} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }} className="stack">
          <Field label="גודל פונט מינימלי ברירת מחדל (px)" hint="מותג יכול להגדיר ערך משלו"><input type="number" className="ltr" min={4} max={40} value={checks.defaultMinFontPx} onChange={(e) => setChecks({ ...checks, defaultMinFontPx: Number(e.target.value) })} /></Field>
          <Field label="תפקידי שכבות שמותר להם לחפוף" hint="מופרד בפסיקים, לדוגמה: background, image"><input className="ltr" value={checks.rolesText} onChange={(e) => setChecks({ ...checks, rolesText: e.target.value })} /></Field>
        </fieldset>
        <ErrorBox error={c.error} />
        {admin ? <div><button className="btn primary" disabled={c.busy}>{c.busy ? 'שומר…' : 'שמירה'}</button></div> : <div className="small muted">עריכה — למנהל מערכת בלבד.</div>}
      </form>
    </div>
  );
}

function BackupCard({ backup, admin, onDone }: { backup: NonNullable<SettingsData['backup']>; admin: boolean; onDone: () => void }) {
  const act = useAction();
  const last = backup.last;
  const run = async () => { if (await act.run(() => api.post('/api/backup/run'), 'הגיבוי הסתיים')) onDone(); };
  return (
    <section className="card stack" aria-labelledby="bk-h">
      <h2 id="bk-h" style={{ margin: 0 }}>גיבוי</h2>
      <div className="small">תיקייה: <span className="ltr mono">{backup.dir}</span></div>
      <div className="small">{backup.intervalHours > 0 ? `גיבוי אוטומטי כל ${backup.intervalHours} שעות, כולל ניסוי שחזור` : 'גיבוי אוטומטי כבוי'}</div>
      {last ? (
        <div className={`alert ${last.ok ? 'ok' : 'err'}`}>
          גיבוי אחרון: {fmtDate(last.at)} · {last.ok ? 'הצליח ושוחזר בהצלחה בבדיקה' : 'נכשל'} · {last.files} קבצים
          {last.error && <div className="small">{last.error}</div>}
          {last.problems.length > 0 && <ul className="small">{last.problems.map((p) => <li key={p}>{p}</li>)}</ul>}
        </div>
      ) : <div className="alert warn">עדיין לא בוצע גיבוי.</div>}
      <ErrorBox error={act.error} />
      {admin && <div><button className="btn" onClick={run} disabled={act.busy}>{act.busy ? 'מגבה…' : 'גיבוי עכשיו'}</button></div>}
    </section>
  );
}
